/** @namespace netdirector-frontend-components */

import defaultConfig from '../netdirector-frontend-components/config';
import DelayedExecutionHelper from '../common/helpers/DelayedExecutionHelper.js';
import FrontendComponents from '../common/contexts/FrontendComponents';
import Gateway from '../common/Gateway';
import MyAccount from '../common/contexts/MyAccount2';
import ObjectDataSearchTagger from '../common/ObjectDataSearchTagger';
import OnlineSales2 from '../common/contexts/OnlineSales2';
import PageView from '../common/events/PageView';
import PerformanceTiming from '../common/contexts/PerformanceTiming';
import ReturningUsersBuilder from "./contexts/builders/ReturningUsers";
import VehicleFinanceCalculator from '../common/events/unstructured/VehicleFinanceCalculator';

/**
 * Class for set up contexts and sending events to Snowplow dedicated for Frontend Components Plugin project.
 * @memberof netdirector-frontend-components
 * @extends ObjectDataSearchTagger
 */
export default class Tagger extends ObjectDataSearchTagger {
  constructor(trackerConfig) {
    super(defaultConfig, trackerConfig);
    this.returningUserBuilder = new ReturningUsersBuilder();
    if (!Gateway.isAutoV10 && this.returningUserBuilder.isEnabled()) {
      this.returningUserBuilder.setContext(this.contexts);
    }
  }

  /**
   * Adding Online Sales Context
   * @param {object} data
   * @param {string} data.step - current OVS step
   * @param {string|null} data.previousStep - previous OVS step
   * @param {string} data.entryPoint - current OVS journey entry point
   * @param {string|null} data.paymentMethod
   * @param {array<string>|null} data.extrasList - extras included in OVS
   * @param {integer|null} data.extrasValue - included extras in total
   * @param {string|null} data.deliveryMethod - selected delivery method
   * @param {integer|null} data.deliveryValue - delivery value
   * @param {string|null} data.provider - payment provider
   * @param {string|null} data.option
   * @param {boolean|null} data.seen - was part exchange seen
   * @param {boolean|null} data.usedForQuote - was part exchange set in quote
   */
  addOnlineSalesContext(data) {
    const context = new OnlineSales2(data.step, data.previousStep, data.entryPoint, data.paymentMethod, data.extrasList, data.extrasValue, data.deliveryMethod, data.deliveryValue, data.provider, data.option, data.seen, data.usedForQuote);
    this.snowplow.globalContexts.update(context);
  }

  /**
   * Adding Frontend Components Context
   * @param {object} component
   * @param {string} component.name
   * @param {string} component.ui
   */
  addFrontendComponentsContext(component) {
    const context = new FrontendComponents(component);
    this.contexts.replace(context);
  }

  /**
   * Tagging an unstructured event - Vehicle Finance Calculator
   * @param {object} data
   * @param {string|null} data.provider
   * @param {integer|null} data.odometerAnnual -  vehicle odometer annual
   * @param {('miles'|'km'|null)} data.odomoterUnit - vehicle odometer unit
   * @param {integer|null} data.term - finance term
   * @param {integer|null} data.deposit - finance deposit
   * @param {integer|null} data.monthly - finance monthly
   * @param {string|null} data.currency - 3 characters i.e. 'USD'
   * @param {string|null} data.type - finance type
   */
  trackFinanceCalculatorEvent(data) {
    data = {
      fi_pr: data.provider,
      fi_ve_od_an: data.odometerAnnual,
      fi_ve_od_un: data.odometerUnit,
      fi_te: data.term,
      fi_de: data.deposit,
      fi_mo: data.monthly,
      fi_cu: data.currency,
      fi_ty: data.type
    }
    const event = new VehicleFinanceCalculator(this.snowplow);
    event.setContexts(this.contexts);
    event.trackInteraction(data);
  }

  /**
   * Attach data to Returning Users Context
   * This data will be merged to current Returning User Context Data and stored on below cookies
   * sa_enquiries_sent, sa_last_viewed_vehicles, sa_last_viewed_vehicles, sa_session_counter
   * @param {object} data
   * @param {array} data.lastViewedVehicles - IDs of last viewed vehicles
   * @param {boolean} data.hasSearched - True/False value if user has searched
   * @param {array} data.sentEnquiries - Sent Enquiry Ids
   */
  storeReturningUserContextData(data) {
    this.returningUserBuilder.lastViewedVehicles = data.lastViewedVehicles;
    this.returningUserBuilder.hasSearched = data.hasSearched;
    this.returningUserBuilder.sentEnquiries = data.sentEnquiries;
    if (!Gateway.isAutoV10 && this.returningUserBuilder.isEnabled()) {
      this.returningUserBuilder.setContext(this.contexts);
    }
  }

  /**
   * Tagging a PageView event
   */
  trackPageView() {
    const event = new PageView(this.snowplow);
    event.setData(document.title);
    event.setContexts(this.contexts);
    if (!PerformanceTiming.shouldBeAdded)
    {
      event.send();
    } else {
      DelayedExecutionHelper.delay(
        () => PerformanceTiming.shouldBeDelayed,
        () => {
          event.addContext(new PerformanceTiming());
          event.send();
        }
      );
    }
  }

  /**
   * Adding My Account Context
   * @param {object} data
   * @param {string|null} data.userHash - user hash
   * @param {('guest'|'member')} data.userType - user type
   * @param {integer|null} data.loginsNumber - number of logins
   * @param {array<string>|null} data.vehicleList - vehicle ids
   * @param {array<string>|null} data.purchases - purchases
   * @param {array<string>|null} data.savedVehicles - saved vehicles
   */
  addMyAccountContext(data) {
    const contextData = {
      user: {
        user_hash: data.userHash,
        user_type: data.userType,
        number_of_logins: parseInt(data.loginsNumber)
      },
      quotes: {
        vehicle_list: data.vehicleList,
      },
      purchases: {
        vehicle_list: data.purchases,
      },
      saved_vehicles: {
        vehicle_list: data.savedVehicles,
      }
    };
    const context = new MyAccount(contextData);
    this.contexts.add(context);
  }
}
